import { cva } from 'class-variance-authority';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class handling
 */
export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

/**
 * Generate initials from name or email
 */
export function getInitials(nameOrEmail: string): string {
  if (!nameOrEmail) {
    return '?';
  }

  // If it's an email, use the first letter before @
  if (nameOrEmail.includes('@')) {
    return nameOrEmail.charAt(0).toUpperCase();
  }

  // If it's a name, get first letter
  const trimmed = nameOrEmail.trim();
  if (trimmed.length === 0) {
    return '?';
  }

  // Try to get first letter of each word (up to 2 letters)
  const words = trimmed.split(/\s+/);
  if (words.length >= 2 && words[0]?.[0] && words[1]?.[0]) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  return trimmed.charAt(0).toUpperCase();
}

/**
 * Extract the initial letter from a person's name.
 * Handles single names, multiple names, and edge cases.
 * @param name - The person's name (e.g., "John Doe", "Tanisha Garg", "John")
 * @returns The initial letter (uppercase) or '?' if name is invalid
 */
export function extractNameInitial(name: string | null | undefined): string {
  if (!name || name.trim().length === 0) {
    return '?';
  }

  const trimmedName = name.trim();
  // Get the first character of the first word
  const firstChar = trimmedName.charAt(0);

  // Return uppercase initial or '?' if not a letter
  return firstChar.match(/[a-zA-Z]/) ? firstChar.toUpperCase() : '?';
}

/**
 * Format ISO date string to a readable timestamp, respecting the user's
 * locale and timezone. Both args fall back to the browser default when
 * undefined.
 */
export function formatTimestamp(isoString: string, locale?: string, timeZone?: string): string {
  if (!isoString) {
    return '';
  }
  const date = new Date(isoString);
  return date.toLocaleString(locale || undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
}

export const buttonVariants = cva(
  "inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-md text-xs font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        icon: 'size-9',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
      },
      variant: {
        default:
          'bg-primary-active text-primary-on-button text-xs shadow-xs font-medium hover:bg-primary-hover disabled:bg-primary-disabled',
        destructive:
          'bg-destructive text-primary-on-button shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary-active underline-offset-4 hover:underline hover:text-primary-hover disabled:text-primary-disabled',
        outline:
          'border border-border-subtle bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-primary-on-button shadow-xs hover:bg-secondary/80',
      },
    },
  },
);

export const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
      },
    },
  },
);
